// Copyright (c) Ping. All rights reserved.
//
// Shared signal between the active-call reload guard (CallHost) and the call
// store. When the browser shows its native "Reload site?" confirmation, the tab
// is briefly suspended and LiveKit can emit a transient `disconnected` — if the
// user then clicks Cancel, that stale disconnect must NOT tear the call down.
// CallHost stamps `markReloadAttempt()` when a reload/unload is being attempted;
// the store consults `isReloadAttemptRecent()` and treats a disconnect inside the
// window as reconnecting rather than terminal, so a cancelled reload keeps the
// call alive.

let lastReloadAttemptAt = 0;

// How long after a reload attempt a LiveKit disconnect is treated as a dialog-
// induced transient (reconnecting) instead of a terminal teardown. Comfortably
// covers how long a user typically lingers on the native confirm dialog.
const RELOAD_ATTEMPT_GRACE_MS = 8000;

/** Stamp that a reload/tab-close is being attempted (native dialog may show). */
export function markReloadAttempt(): void {
  // `Date.now` is fine here — this is UI-thread timing, not persisted state.
  lastReloadAttemptAt = Date.now();
}

/** Clear the mark — call when the page provably survived (reload was cancelled),
 *  so a genuinely-later disconnect is treated terminally again. */
export function clearReloadAttempt(): void {
  lastReloadAttemptAt = 0;
}

/** True when a reload was attempted within the grace window — a LiveKit
 *  `disconnected` in this window is a dialog-induced transient, not a real end. */
export function isReloadAttemptRecent(): boolean {
  return lastReloadAttemptAt > 0 && Date.now() - lastReloadAttemptAt < RELOAD_ATTEMPT_GRACE_MS;
}
